import React from "react";
import {
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  CircularProgress,
  Divider,
  RadioGroup,
  Radio,
  Paper,
  Tabs,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Backdrop } from "@mui/material";
import { Formik, setNestedObjectValues } from "formik";
import CustomTab from "src/components/Tabs/customTab";
import GeneralDialog from "src/components/GeneralDialog";
import * as Yup from "yup";
import clsx from "clsx";
import BranchSelect from "../../Select/branchSelect";
import ProductSelect from "../../Select/productSelect";
import SnFormatSelect from "../../Select/snFormatSelect";
import CustomRangePanel from "../customRangePanel";
import CustomSerialNumberRangePanel from "../customSerialNumberRangePanel";
import SnAvailabilityListTable from "../../table/snAvailabilityListTable";

export const SERIAL_NUMBER_TYPE_NUMBER = "number";
export const SERIAL_NUMBER_TYPE_ALPHANUMERIC = "alphanumeric";

const useStyles = makeStyles((theme) => ({
  accessBox: {
    display: "flex",
    flexFlow: "column",
  },
  scrollContainer: {
    maxHeight: "70vh",
    overflowY: "auto",
    backgroundColor: "#f3f3f3",
    position: "relative", // Allow absolute positioned dropdowns
  },
  root: {
    backgroundColor: "#ffffff",
  },
  backdrop: {
    zIndex: "1500",
    color: "#fff",
  },
  unselectedBox: {
    border: "2px solid #D0D5DD",
    borderRadius: 8,
    padding: "8px 16px 8px 8px",
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: "#FFFFFF",
  },
  selectedBox: {
    border: "2px solid #6AAF68",
    borderRadius: 8,
    padding: "8px 16px 8px 8px",
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: "#F7FBF7",
  },
  typeRadioGroup: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: theme.spacing(1),
  },
  disable: {
    opacity: 0.5,
  },
  enable: {
    opacity: 1,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Typography>
  );
}

export default function AssignPanelComponent({
  isUpdating,
  selectedCompany,
  onSerialNumberAssignDialogCloseClick,
  handleSubmit,
  isBackdropOpen,
  checkSNAvailability,
  isSNexceedRange,
  resetAvailabilityList,
  formRef,
  viewReassignHistory,
}) {
  const classes = useStyles();

  function a11yProps(index, label = "label") {
    return {
      id: `scrollable-auto-tab-${index}`,
      "aria-controls": `scrollable-auto-tab-${index}`,
      label: (
        <Typography variant="button" style={{ textTransform: "none" }}>
          {label}
        </Typography>
      ),
    };
  }

  if (!selectedCompany) {
    return null;
  }
  return (
    <>
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={{
          branch: null,
          product: null,
          format: "",
          formatType: SERIAL_NUMBER_TYPE_NUMBER,
          quantity: 0,
          isCustomQuantity: false,
          generateRangeType: "sequence_number",
          generateRanges: [
            {
              start: "",
              end: "",
            },
          ],
          preview: false,
          forceReassign: false,
          showConfirmationDialog: false,
        }}
        validationSchema={Yup.object().shape({
          branch: Yup.mixed().required("Required"),
          product: Yup.mixed().required("Required"),
          format: Yup.mixed().required("Required"),
          generateRanges: Yup.array().of(
            Yup.object().shape({
              start: Yup.number().required("Required"),
              end: Yup.number().min(
                Yup.ref("start"),
                "Number must be greater than or equal to start from"
              ),
            })
          ),
        })}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers className={classes.scrollContainer}>
              <Box className={classes.accessBox}>
                <Box>
                  <Box>
                    <Box mb={0.5}>
                      <Typography variant="body1" color="textSecondary">
                        <b>Product</b>
                      </Typography>
                    </Box>
                    <Box>
                      <ProductSelect
                        placeholder="Select Product"
                        selectedCompany={selectedCompany}
                        isBranchSelected={!!formik.values.branch}
                        handleProductChange={(value) => {
                          formik.resetForm();
                          formik.setFieldValue("product", value);
                          resetAvailabilityList();
                        }}
                        selectedProduct={formik.values.product}
                        fetchAllProduct
                      />
                    </Box>
                  </Box>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Box
                    className={clsx({
                      [classes.disable]: !formik.values.product,
                      [classes.enable]: !!formik.values.product,
                    })}
                  >
                    <Box mb={0.5}>
                      <Typography variant="body1" color="textSecondary">
                        <b>Branch</b>
                      </Typography>
                    </Box>
                    <Box>
                      <BranchSelect
                        placeholder="Select Branch"
                        selectedCompany={selectedCompany}
                        handleBranchChange={(value) => {
                          formik.setFieldValue("branch", value);
                          formik.setFieldValue(
                            "generateRangeType",
                            "sequence_number"
                          );
                          formik.setFieldValue("format", null);
                          formik.setFieldValue("preview", false);
                          resetAvailabilityList();
                        }}
                        selectedBranch={formik.values.branch}
                        selectedProduct={formik.values.product}
                        disabled={!formik.values.product}
                      />
                    </Box>
                  </Box>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Box
                    className={clsx({
                      [classes.disable]: !formik.values.branch,
                      [classes.enable]: !!formik.values.branch,
                    })}
                  >
                    <Box mb={0.5}>
                      <Typography variant="body1" color="textSecondary">
                        <b>Serial Number Pattern</b>
                      </Typography>
                    </Box>
                    <Box>
                      <SnFormatSelect
                        selectedBranch={formik.values.branch}
                        selectedFormat={formik.values.format}
                        handleSelect={(value) => {
                          formik.setFieldValue("format", value.format);
                          formik.setFieldValue(
                            "formatType",
                            value.generatingType
                          );
                          formik.setFieldValue("preview", false);
                          formik.setFieldValue("generateRanges", [
                            {
                              start: "",
                              end: "",
                            },
                          ]);
                          formik.setFieldValue(
                            "generateRangeType",
                            "sequence_number"
                          );
                          resetAvailabilityList();
                        }}
                      />
                    </Box>
                  </Box>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Box
                    className={clsx({
                      [classes.disable]: !formik.values.format,
                      [classes.enable]: !!formik.values.format,
                    })}
                  >
                    <Box mb={0.5}>
                      <Typography variant="body1" color="textSecondary">
                        <b>Range</b>
                      </Typography>
                    </Box>
                    <Box>
                      <Paper elevation={0} style={{ padding: "4px 16px 16px" }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          style={{ backgroundColor: "white" }}
                        >
                          <Tabs
                            value={
                              formik.values.generateRangeType ===
                              "sequence_number"
                                ? 0
                                : 1
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue(
                                "generateRangeType",
                                value === 0
                                  ? "sequence_number"
                                  : "serial_number"
                              );
                              formik.setFieldValue("generateRanges", [
                                {
                                  start: "",
                                  end: "",
                                },
                              ]);
                            }}
                            aria-label="scrollable auto tabs example"
                            textColor="primary"
                            variant="scrollable"
                            indicatorColor="primary"
                          >
                            {(formik.values.formatType ===
                            SERIAL_NUMBER_TYPE_ALPHANUMERIC
                              ? ["Sequence Number"]
                              : ["Sequence Number", "Serial Number"]
                            ).map((item, index) => (
                              <CustomTab
                                key={index}
                                value={index}
                                disabled={
                                  (item === "Serial Number" &&
                                    formik.values.type ===
                                      SERIAL_NUMBER_TYPE_ALPHANUMERIC) ||
                                  isUpdating
                                }
                                {...a11yProps(index, item)}
                              />
                            ))}
                          </Tabs>
                        </Box>
                        <Divider />
                        <Box py={1}>
                          {(formik.values.formatType ===
                          SERIAL_NUMBER_TYPE_ALPHANUMERIC
                            ? ["Sequence Number"]
                            : ["Sequence Number", "Serial Number"]
                          ).map((item, index) => (
                            <TabPanel
                              value={
                                formik.values.generateRangeType ===
                                "sequence_number"
                                  ? 0
                                  : 1
                              }
                              index={index}
                              key={index}
                            >
                              <Box>
                                {item === "Sequence Number" && (
                                  <CustomRangePanel
                                    formik={formik}
                                    disabled={
                                      !formik.values.format || isUpdating
                                    }
                                    updateQuantity={(quantity) => {
                                      formik.setFieldValue(
                                        "quantity",
                                        quantity
                                      );
                                    }}
                                    checkSNAvailability={(snRange) => {
                                      checkSNAvailability(formik, snRange);
                                    }}
                                    isSNexceedRange={isSNexceedRange}
                                  />
                                )}
                                {item === "Serial Number" && (
                                  <CustomSerialNumberRangePanel
                                    formik={formik}
                                    disabled={
                                      !formik.values.format || isUpdating
                                    }
                                    type="serial_number"
                                    updateQuantity={(quantity) => {
                                      formik.setFieldValue(
                                        "quantity",
                                        quantity
                                      );
                                    }}
                                    checkSNAvailability={(snRange) => {
                                      checkSNAvailability(formik, snRange);
                                    }}
                                    isSNexceedRange={isSNexceedRange}
                                  />
                                )}
                              </Box>
                            </TabPanel>
                          ))}
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Box
                    className={clsx({
                      [classes.disable]: !formik.values.preview,
                      [classes.enable]: !!formik.values.preview,
                    })}
                  >
                    <Box mb={0.5}>
                      <Typography variant="body1" color="textSecondary">
                        <b>Preview</b>
                      </Typography>
                    </Box>
                    <Box>
                      <SnAvailabilityListTable
                        handleReload={() => checkSNAvailability(formik)}
                        format={formik.values.format}
                        activeType={formik.values.type}
                        formik={formik}
                        selectedProduct={formik.values.product}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                p={1}
              >
                <Box display="flex" alignItems="center">
                  <Button variant="text" onClick={viewReassignHistory}>
                    View Reassign History
                  </Button>
                  <Box ml={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formik.values.forceReassign}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "forceReassign",
                              e.target.checked
                            )
                          }
                          disabled={isUpdating}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            Force reassign scanned QR codes
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Updates consumer-facing product info on
                            already-scanned codes.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center">
                  <Button
                    variant="text"
                    type="button"
                    disabled={isUpdating}
                    onClick={onSerialNumberAssignDialogCloseClick}
                    style={{ color: "black" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={
                      isUpdating ||
                      !formik.isValid ||
                      (formik.values.preview && !Number(formik.values.quantity))
                    }
                    onClick={async () => {
                      const errors = await formik.validateForm();

                      if (Object.keys(errors).length === 0) {
                        formik.setFieldValue("showConfirmationDialog", true);
                      } else {
                        formik.setTouched(setNestedObjectValues(errors, true));
                      }
                    }}
                  >
                    Update
                  </Button>
                </Box>
              </Box>
            </DialogActions>
            <GeneralDialog
              isOpen={formik.values.showConfirmationDialog}
              handleClose={() => {
                formik.setFieldValue("showConfirmationDialog", false);
              }}
              handleProceed={() => {
                formik.setFieldValue("showConfirmationDialog", false);
                formik.handleSubmit();
              }}
              title="Confirm Assign Serial Number"
              description={
                formik.values.forceReassign
                  ? "Force reassign is enabled: scanned QR codes will also be reassigned, and the consumer-facing product info for those codes will be updated. Serial numbers without a product will be assigned to the selected product. Serial numbers already linked to another product will be reassigned. Are you sure you want to proceed?"
                  : "Serial numbers without a product will be assigned to the selected product. Serial numbers already linked to another product will be reassigned to the selected product. Are you sure you want to proceed?"
              }
              type="danger"
            />
          </form>
        )}
      </Formik>
      <Backdrop open={isBackdropOpen} className={classes.backdrop}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Box>
            <CircularProgress color="inherit" />
          </Box>
          <Box pt={1}>
            <Typography>Processing...</Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
}
